import { generateShortId } from '@o2d/shared';
import { creationDefaults } from '../../core/viewport/zoom-cluster.service.js';
import type { StickyNote } from './sticky.interface.js';

const DEFAULT_STICKY_COLOR = '#fde68a';

/**
 * Freeze the payload in DEV builds so accidental mutations surface as a
 * TypeError instead of silently corrupting the readonly StickyNote contract.
 * In production builds the freeze is skipped to avoid the small per-call cost.
 */
function freezeInDev<T extends object>(value: T): T {
  if (import.meta.env?.DEV) {
    return Object.freeze(value);
  }
  return value;
}

/**
 * Stefan, 2026-09-03 — "choose properties before drawing": optional overrides
 * for the pre-placement-staged options-circle values. Mirrors
 * `CreateRectangleDefaultsOptions`.
 */
export interface CreateStickyDefaultsOptions {
  readonly color?: string;
}

/**
 * Build a fresh sticky-note default payload — extracted from the inline
 * `placeStickyAt` body in `o2d-whiteboard.element.ts` per TR4.
 *
 * - `sizeOpt === null`: bare-click branch, sticky is centred on the pointer
 *   (top-left = point - size/2). Side length scales inversely with viewport
 *   scale via `creationDefaults`.
 * - `sizeOpt !== null`: drag-to-create branch, the dragged box is the exact
 *   placement rectangle (top-left = pointer).
 */
export function createStickyDefaults(
  viewportScale: number,
  point: { x: number; y: number },
  sizeOpt: { width: number; height: number } | null,
  options?: CreateStickyDefaultsOptions,
): StickyNote {
  const cd = creationDefaults(viewportScale);
  const w = sizeOpt ? sizeOpt.width : cd.stickySize;
  const h = sizeOpt ? sizeOpt.height : cd.stickySize;
  const x = sizeOpt ? point.x : point.x - w / 2;
  const y = sizeOpt ? point.y : point.y - h / 2;
  const sticky: StickyNote = {
    id: generateShortId(),
    type: 'sticky',
    x,
    y,
    width: w,
    height: h,
    rotation: 0,
    text: '',
    color: options?.color ?? DEFAULT_STICKY_COLOR,
  };
  return freezeInDev(sticky);
}
